import React, { Suspense, useEffect, useRef } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { App as AntApp, ConfigProvider } from 'antd'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { persistQueryClient } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useAtomValue } from 'jotai'

import AppRoutes from './routes'
import Loading from '@/components/Loading'
import useOrganization from '@/libs/context/organization/useOrganization'
import { useAtom } from 'jotai'
import { themeKeyAtom, themeConfigAtom } from '@/libs/context/theme/themeAtom'
import { useAuth } from '@/libs/context/auth'
import { AuthProvider } from '@/libs/context/auth'
import { ThemeProvider } from '@/libs/theme'
import { isSentryEnabled, Sentry } from '@/libs/sentry'
import { QUERY_CLIENT_CONFIG } from '@/config/reactQuery'

const queryClient = new QueryClient(QUERY_CLIENT_CONFIG)
const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
})
persistQueryClient({
  queryClient,
  persister: localStoragePersister,
  maxAge: 24 * 60 * 60 * 1000, // 24 hours
})

/**
 * Hook personalizado para manejar la sincronización del theme
 * con la organización y permitir override manual desde el selector
 */
const useThemeSync = () => {
  const { organization } = useOrganization()
  const [themeKey, setThemeKey] = useAtom(themeKeyAtom)
  const manualOverrideRef = useRef(false)
  const lastOrgIndustryRef = useRef(null)

  useEffect(() => {
    if (organization) {
      const industry = organization.data.industry
      const organizationTheme = industry || 'default'

      // Si es la primera vez que carga la organización o cambió la industria
      const isFirstLoad = lastOrgIndustryRef.current === null
      const industryChanged = lastOrgIndustryRef.current !== industry

      if (isFirstLoad || industryChanged) {
        // Actualizar el theme solo si no hay override manual o si cambió la industria
        if (!manualOverrideRef.current || industryChanged) {
          setThemeKey(organizationTheme)
          manualOverrideRef.current = false // Reset override cuando cambia org
        }
        lastOrgIndustryRef.current = industry
      }
    }
  }, [organization, setThemeKey])

  // Detectar cuando el usuario cambia manualmente el theme
  useEffect(() => {
    if (organization && lastOrgIndustryRef.current !== null) {
      const organizationTheme = organization.data.industry || 'default'
      if (themeKey !== organizationTheme) {
        manualOverrideRef.current = true
      }
    }
  }, [themeKey, organization])

  return { themeKey, organizationIndustry: organization }
}

function AppRouterContent() {
  useThemeSync()
  const { user } = useAuth()

  useEffect(() => {
    if (isSentryEnabled()) {
      if (user) {
        Sentry.setUser({ id: user.id, email: user.email })
      } else {
        Sentry.setUser(null)
      }
    }
  }, [user])

  return (
    <Suspense fallback={<Loading />}>
      <AppRoutes />
    </Suspense>
  )
}

function AppWithTheme() {
  const themeConfig = useAtomValue(themeConfigAtom)
  return (
    <ConfigProvider theme={themeConfig}>
      <AntApp>
        <AuthProvider>
          <AppRouterContent />
        </AuthProvider>
      </AntApp>
    </ConfigProvider>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <AntApp>
            <AppWithTheme />
          </AntApp>
        </ThemeProvider>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
